import { Fragment, useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useSession } from '../../auth/useSession'
import { Button, ErrorState, Spinner, Tag } from '../../components'
import type { TagTone } from '../../components'
import { getErrorCode } from '../../lib/errorCode'
import { CONFIG_CHANGES_PATH, MEP_MARGIN_HEAT_PATH } from '../../lib/routes'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as styles } from '../admin/AdminPage'
import { CHANGES_TABLE_PARAM } from './ConfigChangesPage'
import local from './MepStaffingPage.module.scss'

type StaffingDay = RouterOutputs['configuration']['mepStaffing']['list'][number]
type Person = StaffingDay['team']['people'][number]
type MepSettings = RouterOutputs['configuration']['mepSettings']['get']['settings']
type Shift = MepSettings['shifts'][number]
type ShiftId = Shift['id']
type DefaultPeople = MepSettings['defaultPeoplePerShift']

/** A day as it is being edited: what Guardar sends. */
type DayDraft = {
  people: Array<Person>
  /** What is typed in each shift's names, as typed: a comma being written is not a name yet. */
  namesText: Record<ShiftId, string>
  closed: boolean
  note: string
  /** «↺ por defecto» or «Abrir el día» on a day with its row: saved as a reset of the day. */
  toDefault: boolean
}

/** How many working days the screen shows, from today. */
const DAYS_SHOWN = 10
/** Calendar days asked for: ten working days fit in three weeks with a holiday week-end to spare. */
const RANGE_DAYS = 20
/** How long «Guardado» stays on screen. */
const SAVED_NOTICE_MS = 2600

const PLANT_ZONE = 'Europe/Madrid'

const SHIFT_IDS: Array<ShiftId> = ['manana', 'tarde']

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const HISTORY_LINK = `${CONFIG_CHANGES_PATH}?${CHANGES_TABLE_PARAM}=mep_day_team`

/** Today in the plant, `YYYY-MM-DD`, whatever the device's zone. */
const plantToday = (): string => new Intl.DateTimeFormat('en-CA', { timeZone: PLANT_ZONE }).format(new Date())

const plantDayOf = (moment: Date): string => new Intl.DateTimeFormat('en-CA', { timeZone: PLANT_ZONE }).format(moment)

const addDays = (day: string, days: number): string => {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, date + days, 12)).toISOString().slice(0, 10)
}

/** «jue 8/10» */
const dayLabel = (day: string): string => {
  const weekday = new Date(`${day}T12:00:00Z`).toLocaleDateString('es-ES', { timeZone: 'UTC', weekday: 'short' }).replace('.', '')
  return `${weekday} ${Number(day.slice(8, 10))}/${Number(day.slice(5, 7))}`
}

/** The ISO week of a day: the «Semana 41» the table groups by. */
const isoWeek = (day: string): number => {
  const date = new Date(`${day}T12:00:00Z`)
  const thursday = new Date(date.getTime() + (3 - ((date.getUTCDay() + 6) % 7)) * 86_400_000)
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1)
  return Math.floor((thursday.getTime() - yearStart) / (7 * 86_400_000)) + 1
}

const clock = (moment: Date): string =>
  moment.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: PLANT_ZONE })

const formatMinutes = (minutes: number): string => {
  const rounded = Math.round(minutes)
  if (rounded < 60) {
    return `${rounded} min`
  }
  const rest = rounded % 60
  return rest === 0 ? `${Math.floor(rounded / 60)} h` : `${Math.floor(rounded / 60)} h ${String(rest).padStart(2, '0')}`
}

const rangeText = ([lowest, highest]: [number, number]): string => (lowest === highest ? `${lowest}` : `${lowest}–${highest}`)

type Balance = { label: string; tone: TagTone }

/** Falta / sobra / justo, against the people needed. */
const balanceOf = (needed: number, present: number): Balance => {
  if (present < needed) {
    return { label: `falta${needed - present > 1 ? 'n' : ''} ${needed - present}`, tone: 'danger' }
  }
  if (present > needed) {
    return { label: `sobra${present - needed > 1 ? 'n' : ''} ${present - needed}`, tone: 'info' }
  }
  return { label: 'justo', tone: 'ok' }
}

const peopleOf = (people: Array<Person>, shiftId: ShiftId): Array<Person> => people.filter((person) => person.shiftId === shiftId)

const namesOf = (people: Array<Person>, shiftId: ShiftId): string => peopleOf(people, shiftId).map((person) => person.name).join(', ')

const namesTextOf = (people: Array<Person>): Record<ShiftId, string> => ({ manana: namesOf(people, 'manana'), tarde: namesOf(people, 'tarde') })

/** «Mañana 1» … «Mañana N»: the people a day nobody set has, as the API names them. */
const defaultPeopleOf = (defaults: DefaultPeople, shifts: Array<Shift>): Array<Person> =>
  shifts.flatMap((shift) => Array.from({ length: defaults[shift.id] }, (_, index) => ({
    id: `${shift.id}-${index + 1}`,
    name: `${shift.name} ${index + 1}`,
    shiftId: shift.id,
    startsAt: null,
  })))

/** The day as loaded: a day with no row follows the default being edited. */
const loadedDraft = (day: StaffingDay, defaults: DefaultPeople, shifts: Array<Shift>): DayDraft => {
  const people = day.team.version === 0 ? defaultPeopleOf(defaults, shifts) : day.team.people
  return { people, namesText: namesTextOf(people), closed: day.team.closed, note: day.team.note ?? '', toDefault: false }
}

const savedForm = (draft: DayDraft): string => JSON.stringify({
  people: draft.people.map(({ id, name, shiftId, startsAt }) => ({ id, name: name.trim(), shiftId, startsAt })),
  closed: draft.closed,
  note: draft.note.trim(),
  toDefault: draft.toDefault,
})

/** What is wrong with a day's draft, in the plant's words; null when it can be saved. */
const draftProblem = (workDate: string, draft: DayDraft): string | null => {
  if (draft.closed && draft.note.trim() === '') {
    return `Falta el motivo del festivo del ${dayLabel(workDate)}.`
  }
  if (draft.people.some((person) => person.name.trim() === '' || person.name.trim().length > 40)) {
    return `Revisa los nombres del ${dayLabel(workDate)}: cada uno entre 1 y 40 letras.`
  }
  return null
}

const kitchenPlanText = (day: StaffingDay, draft: DayDraft): { text: string; tone: 'ok' | 'warn' | 'off' } => {
  if (draft.closed) {
    return { text: day.tasks === 0 ? 'festivo · sin MOs' : `festivo · ${day.tasks} tareas`, tone: 'off' }
  }
  const { state, generatedAt } = day.kitchenPlan
  if (state === 'generado' && generatedAt !== null) {
    const when = plantDayOf(generatedAt) === day.workDate ? clock(generatedAt) : `${dayLabel(plantDayOf(generatedAt)).split(' ')[1]} ${clock(generatedAt)}`
    return { text: `generado ${when}`, tone: 'ok' }
  }
  return state === 'provisional' ? { text: 'provisional · MOs de MRPeasy', tone: 'warn' } : { text: 'sin MOs todavía', tone: 'off' }
}

type StepperProps = {
  value: number
  disabled: boolean
  lessLabel: string
  moreLabel: string
  countLabel: string
  onLess: () => void
  onMore: () => void
  big?: boolean
}

/**
 * − n +, compact: the kit's Button is the 48px pill of the plant tablets, and
 * this is an administration table on a computer where ten days have to fit
 * (MEP-P C, `config.html`).
 */
const Stepper = ({ value, disabled, lessLabel, moreLabel, countLabel, onLess, onMore, big = false }: StepperProps): ReactElement => (
  <span className={[local['mep-staffing__step'], big ? local['mep-staffing__step--big'] : ''].join(' ')}>
    <button type="button" aria-label={lessLabel} disabled={disabled || value === 0} onClick={onLess}>−</button>
    <span aria-label={countLabel}>{value}</span>
    <button type="button" aria-label={moreLabel} disabled={disabled} onClick={onMore}>+</button>
  </span>
)

type DayRowsProps = {
  day: StaffingDay
  draft: DayDraft
  shifts: Array<Shift>
  today: string
  selected: boolean
  edited: boolean
  noteOpen: boolean
  readOnly: boolean
  onSelect: () => void
  onChange: (draft: DayDraft) => void
  onDefault: () => void
  onOpenNote: (open: boolean) => void
}

/** One day: a row per shift, with the day, its kitchen plan and its note across both. */
const DayRows = ({ day, draft, shifts, today, selected, edited, noteOpen, readOnly, onSelect, onChange, onDefault, onOpenNote }: DayRowsProps): ReactElement => {
  const label = dayLabel(day.workDate)
  const plan = kitchenPlanText(day, draft)
  const withoutData = !draft.closed && day.kitchenPlan.state === 'sin_mos'
  const short = !draft.closed && !withoutData && day.staffing.shifts.some((shift) => peopleOf(draft.people, shift.shiftId === 'tarde' ? 'tarde' : 'manana').length < shift.needed)
  const atDefault = draft.toDefault || (!edited && day.team.source === 'defecto')

  const setShift = (shiftId: ShiftId, people: Array<Person>, text = namesOf(people, shiftId)): void =>
    onChange({
      ...draft,
      toDefault: false,
      people: SHIFT_IDS.flatMap((id) => (id === shiftId ? people : peopleOf(draft.people, id))),
      namesText: { ...draft.namesText, [shiftId]: text },
    })
  const add = (shift: Shift): void => {
    const current = peopleOf(draft.people, shift.id)
    setShift(shift.id, [...current, { id: '', name: `${shift.name} ${current.length + 1}`, shiftId: shift.id, startsAt: null }])
  }
  const remove = (shiftId: ShiftId): void => setShift(shiftId, peopleOf(draft.people, shiftId).slice(0, -1))
  const rename = (shiftId: ShiftId, text: string): void => {
    // By position, from the draft or else from the day as loaded: clearing the
    // field and typing the names again keeps whom each place pointed to.
    const current = peopleOf(draft.people, shiftId)
    const loaded = peopleOf(day.team.people, shiftId)
    const names = text.split(',').map((name) => name.trim()).filter((name) => name !== '')
    setShift(shiftId, names.map((name, index) => {
      const holder = current[index] ?? loaded[index]
      return { id: holder?.id ?? '', name, shiftId, startsAt: holder?.startsAt ?? null }
    }), text)
  }
  const close = (): void => {
    onChange({ ...draft, closed: true, toDefault: false, people: [], namesText: { manana: '', tarde: '' } })
    onOpenNote(true)
  }

  const dayCellClasses = [
    local['mep-staffing__day-cell'],
    selected ? local['mep-staffing__day-cell--selected'] : '',
    short ? local['mep-staffing__day-cell--short'] : '',
  ].join(' ')
  const rowClasses = (first: boolean): string => [
    local['mep-staffing__row'],
    first ? local['mep-staffing__row--first'] : '',
    selected ? local['mep-staffing__row--selected'] : '',
    draft.closed ? local['mep-staffing__row--off'] : '',
  ].join(' ')
  const showNoteEditor = !readOnly && (noteOpen || (draft.closed && draft.note.trim() === ''))
  const noteLabel = draft.closed ? `Motivo del festivo el ${label}` : `Nota del día el ${label}`

  return (
    <>
      {shifts.map((shift, index) => {
        const staffing = day.staffing.shifts.find((one) => one.shiftId === shift.id)
        const people = peopleOf(draft.people, shift.id)
        const balance = balanceOf(staffing?.needed ?? 0, people.length)
        const first = index === 0
        return (
          <tr key={shift.id} className={rowClasses(first)} onClick={onSelect}>
            {first
              ? (
                <th scope="row" rowSpan={shifts.length} className={dayCellClasses}>
                  <button type="button" className={local['mep-staffing__day']} onClick={onSelect} aria-pressed={selected}>{label}</button>
                  {day.workDate === today ? <span className={local['mep-staffing__today']}>hoy</span> : null}
                  {draft.note.trim() !== '' && !showNoteEditor ? <span className={local['mep-staffing__day-note']}>{draft.note.trim()}</span> : null}
                  {draft.closed
                    ? null
                    : (atDefault
                      ? <span className={local['mep-staffing__source']}>por defecto</span>
                      : (
                        <span className={local['mep-staffing__source']}>
                          <span className={local['mep-staffing__set']}>fijado</span>
                          {readOnly ? null : <button type="button" className={local['mep-staffing__link']} onClick={onDefault}>↺ por defecto</button>}
                        </span>
                      ))}
                  {/* Only the chosen day offers them: ten days of links would bury the figures. */}
                  {readOnly || !selected
                    ? null
                    : (
                      <span className={local['mep-staffing__day-actions']}>
                        {draft.closed
                          ? <button type="button" className={local['mep-staffing__link']} aria-label={`Abrir el día ${label}`} onClick={onDefault}>abrir el día</button>
                          : <button type="button" className={local['mep-staffing__link']} aria-label={`Festivo el ${label}`} onClick={close}>festivo</button>}
                        <button type="button" className={local['mep-staffing__link']} aria-label={draft.closed ? `Motivo del ${label}` : `Nota del ${label}`} onClick={() => onOpenNote(!noteOpen)}>
                          {draft.closed ? 'motivo' : 'nota'}
                        </button>
                      </span>
                    )}
                </th>
              )
              : null}
            {first
              ? (
                <td rowSpan={shifts.length} className={local['mep-staffing__plan']}>
                  <span className={[local['mep-staffing__dot'], local[`mep-staffing__dot--${plan.tone}`]].join(' ')} />
                  {plan.text}
                </td>
              )
              : null}
            <td className={local['mep-staffing__hired']}>
              <span className={local['mep-staffing__shift']}>{shift.name}</span>
              <Stepper
                value={people.length}
                disabled={readOnly || draft.closed}
                lessLabel={`Quitar una persona de ${shift.name} el ${label}`}
                moreLabel={`Añadir una persona a ${shift.name} el ${label}`}
                countLabel={`${shift.name}: ${people.length} personas`}
                onLess={() => remove(shift.id)}
                onMore={() => add(shift)}
              />
            </td>
            <td>
              <input
                className={local['mep-staffing__names']}
                aria-label={`Nombres de ${shift.name} el ${label}`}
                value={draft.namesText[shift.id]}
                placeholder={draft.closed ? '—' : 'Sin nombres: se ponen en el tablero'}
                disabled={readOnly || draft.closed}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => rename(shift.id, event.target.value)}
              />
            </td>
            {draft.closed || withoutData
              ? (first
                ? (
                  <>
                    <td rowSpan={shifts.length} className={local['mep-staffing__need']}>—</td>
                    <td rowSpan={shifts.length}><Tag tone="neutral" label={draft.closed ? 'cerrado' : 'sin datos'} /></td>
                  </>
                )
                : null)
              : (
                <>
                  <td className={local['mep-staffing__need']}>{staffing?.needed ?? 0}</td>
                  <td><Tag tone={balance.tone} label={balance.label} /></td>
                </>
              )}
          </tr>
        )
      })}
      {showNoteEditor
        ? (
          <tr className={local['mep-staffing__note-row']}>
            <td colSpan={6}>
              <label className={local['mep-staffing__note-editor']}>
                <span>{draft.closed ? 'Motivo del festivo' : 'Nota del día'}</span>
                <input
                  className={local['mep-staffing__names']}
                  aria-label={noteLabel}
                  value={draft.note}
                  maxLength={80}
                  placeholder={draft.closed ? 'Fiesta Nacional' : 'Javi de vacaciones'}
                  onChange={(event) => onChange({ ...draft, note: event.target.value })}
                />
                <Button variant="secondary" label="Listo" disabled={draft.closed && draft.note.trim() === ''} onClick={() => onOpenNote(false)} />
              </label>
            </td>
          </tr>
        )
        : null}
    </>
  )
}

/** The kitchen plan of the panel's subtitle: «Plan de cocina generado 09:31 · 62 tareas de mise en place». */
const forecastSubtitle = (day: StaffingDay, draft: DayDraft): string => {
  const plan = kitchenPlanText(day, draft)
  return `Plan de cocina ${plan.text} · ${day.tasks} tareas de mise en place`
}

const dayBalance = (day: StaffingDay, draft: DayDraft): Balance => {
  const gaps = day.staffing.shifts.map((shift) => peopleOf(draft.people, shift.shiftId === 'tarde' ? 'tarde' : 'manana').length - shift.needed)
  const short = gaps.filter((gap) => gap < 0).reduce((sum, gap) => sum - gap, 0)
  const over = gaps.filter((gap) => gap > 0).reduce((sum, gap) => sum + gap, 0)
  if (short > 0) {
    return balanceOf(short, 0)
  }
  return over > 0 ? balanceOf(0, over) : balanceOf(0, 0)
}

/**
 * The chosen day's forecast (MEP-P C): the people needed, a bar per hour
 * against the people hired in its shift — red where it lacks, outlined the
 * hour that sets the shift —, how it is computed, the load by group and what
 * MRPeasy plans against the real durations.
 */
const Forecast = ({ day, draft, today }: { day: StaffingDay; draft: DayDraft; today: string }): ReactElement => {
  const label = dayLabel(day.workDate)
  const title = `Previsión · ${label}${day.workDate === today ? ' (hoy)' : ''}`
  const presentOf = (shiftId: string): number => peopleOf(draft.people, shiftId === 'tarde' ? 'tarde' : 'manana').length

  let body: ReactElement
  if (draft.closed) {
    body = (
      <p className={[styles['admin-page__note'], day.tasks > 0 ? styles['admin-page__note--warn'] : ''].join(' ')}>
        {day.tasks === 0
          ? 'Día cerrado: 0 personas fijadas. Sin MOs de mise en place.'
          : `Día cerrado: 0 personas fijadas. MRPeasy tiene ${day.tasks} tareas de mise en place este día.`}
      </p>
    )
  } else if (day.kitchenPlan.state === 'sin_mos') {
    body = (
      <p className={styles['admin-page__note']}>
        Todavía no hay MOs en MRPeasy para este día: la previsión aparece cuando se crean (y se afina al generar el plan de cocina).
      </p>
    )
  } else {
    const balance = dayBalance(day, draft)
    const present = draft.people.length
    const tallest = Math.max(1, ...day.hours.map((hour) => hour.people), ...day.staffing.shifts.map((shift) => presentOf(shift.shiftId))) + 1
    const heaviest = Math.max(1, ...day.minutesByGroup.map((group) => group.minutes))
    const lines = day.staffing.shifts.map((shift) => {
      const ofShift = day.hours.map((hour, index) => ({ hour, index })).filter(({ hour }) => hour.shiftId === shift.shiftId)
      const firstIndex = ofShift[0]?.index ?? 0
      return { shift, left: (firstIndex / day.hours.length) * 100, width: (ofShift.length / day.hours.length) * 100 }
    })
    body = (
      <>
        <div className={local['mep-staffing__big']}>
          <b>{day.staffing.needed}</b>
          <span>personas necesarias · contratadas {present}</span>
          <Tag tone={balance.tone} label={balance.label} />
        </div>
        <div className={local['mep-staffing__chart']}>
          <ol className={local['mep-staffing__bars']}>
            {day.hours.map((hour) => {
              const hired = presentOf(hour.shiftId)
              const lacking = hour.people > hired
              return (
                <li
                  key={hour.from.getTime()}
                  aria-label={`${clock(hour.from)}: ${hour.people} personas, contratadas ${hired}`}
                  data-short={lacking ? 'true' : 'false'}
                  className={[
                    local['mep-staffing__bar'],
                    lacking ? local['mep-staffing__bar--short'] : '',
                    hour.binding ? local['mep-staffing__bar--binding'] : '',
                  ].join(' ')}
                >
                  <span className={local['mep-staffing__bar-value']}>{hour.people > 0 ? hour.people : ''}</span>
                  <span className={local['mep-staffing__bar-fill']} style={{ height: `calc(var(--plot) * ${hour.people / tallest})` }} />
                </li>
              )
            })}
          </ol>
          {lines.map(({ shift, left, width }) => (
            <span
              key={shift.shiftId}
              className={local['mep-staffing__hire']}
              style={{ left: `${left}%`, width: `${width}%`, bottom: `calc(var(--plot) * ${presentOf(shift.shiftId) / tallest})` }}
            >
              <em>contratadas {presentOf(shift.shiftId)}</em>
            </span>
          ))}
        </div>
        <ol className={local['mep-staffing__hours']} aria-hidden="true">
          {day.hours.map((hour) => <li key={hour.from.getTime()}>{clock(hour.from).slice(0, 2)}</li>)}
        </ol>
        <div className={local['mep-staffing__explain']}>
          {day.staffing.shifts.map((shift) => (
            <p key={shift.shiftId}><b>{shift.name}: necesita {shift.needed}.</b> {shift.explanation}</p>
          ))}
        </div>
        <h3 className={local['mep-staffing__mix-title']}>Carga por grupo (duración real)</h3>
        <ul className={local['mep-staffing__mix']}>
          {day.minutesByGroup.map((group) => (
            <li key={group.groupId ?? 'sin-grupo'}>
              <span>{group.name}</span>
              <i style={{ width: `${(group.minutes / heaviest) * 100}%` }} />
              <span>{formatMinutes(group.minutes)}</span>
            </li>
          ))}
        </ul>
        {day.plannedMinutes > 0 && day.plannedMinutes !== day.minutes
          ? (
            <p className={styles['admin-page__note']}>
              {`MRPeasy planifica ${formatMinutes(day.plannedMinutes)}; con las duraciones reales son ${formatMinutes(day.minutes)}`}
              {day.slowestMachine === null
                ? '.'
                : ` (${day.slowestMachine.name}: ${rangeText(day.slowestMachine.realMinutes)} min reales contra ${rangeText(day.slowestMachine.plannedMinutes)} planificados).`}
            </p>
          )
          : null}
      </>
    )
  }

  return (
    <section className={local['mep-staffing__forecast']} aria-label={`Previsión del ${label}`}>
      <h2 className={local['mep-staffing__forecast-title']}>{title}</h2>
      <p className={local['mep-staffing__forecast-sub']}>{forecastSubtitle(day, draft)}</p>
      {body}
    </section>
  )
}

/**
 * Administración → Mise en place → Personas (MEP-P C): how many people work
 * each of the next working days and who, against the people the kitchen plan
 * needs, ten days at a glance. A day nobody set has the default, edited here
 * too; a holiday is closed with its reason. Every change waits in a draft until
 * «Guardar», which saves the default and then each day with the version it
 * loaded: each leaves its line in the configuration history.
 */
export const MepStaffingPage = (): ReactElement => {
  const { isReadOnly } = useSession()
  const navigate = useNavigate()
  const utils = trpc.useUtils()
  const [today] = useState(plantToday)
  const staffingQuery = trpc.configuration.mepStaffing.list.useQuery({ from: today, to: addDays(today, RANGE_DAYS) })
  const settingsQuery = trpc.configuration.mepSettings.get.useQuery()
  const update = trpc.configuration.mepStaffing.update.useMutation()
  const reset = trpc.configuration.mepStaffing.reset.useMutation()
  const updateSettings = trpc.configuration.mepSettings.update.useMutation()

  const [drafts, setDrafts] = useState<Record<string, DayDraft>>({})
  const [defaultDraft, setDefaultDraft] = useState<DefaultPeople | null>(null)
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [noteOpen, setNoteOpen] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  useEffect(() => {
    if (savedAt === null) {
      return undefined
    }
    const timer = setTimeout(() => setSavedAt(null), SAVED_NOTICE_MS)
    return () => clearTimeout(timer)
  }, [savedAt])

  const header = (
    <>
      <AdminPageHeader
        title="Personas de mise en place"
        intro="Cuántas personas hay contratadas cada día en cada turno y quiénes, frente a las que harían falta según el plan de cocina. Un día sin tocar vale el número por defecto. El gestor del día puede cambiar el número y los nombres de hoy desde el tablero."
        actions={<Button variant="secondary" label="Ver historial" onClick={() => navigate(HISTORY_LINK)} />}
      />
      {isReadOnly ? <ReadOnlyNote /> : null}
    </>
  )

  let body: ReactElement
  if (staffingQuery.isPending || settingsQuery.isPending) {
    body = <>{header}<Spinner label="Cargando las personas…" /></>
  } else if (staffingQuery.isError) {
    body = <>{header}<ErrorState error={staffingQuery.error} onRetry={() => void staffingQuery.refetch()} /></>
  } else if (settingsQuery.isError) {
    body = <>{header}<ErrorState error={settingsQuery.error} onRetry={() => void settingsQuery.refetch()} /></>
  } else {
    const { settings } = settingsQuery.data
    const shifts = settings.shifts
    const defaults = defaultDraft ?? settings.defaultPeoplePerShift
    const days = staffingQuery.data.slice(0, DAYS_SHOWN)
    const selected = days.find((day) => day.workDate === selectedDay) ?? days[0]

    const loadedOf = (day: StaffingDay): DayDraft => loadedDraft(day, defaults, shifts)
    const draftOf = (day: StaffingDay): DayDraft => drafts[day.workDate] ?? loadedOf(day)
    const changedDays = days.filter((day) => drafts[day.workDate] !== undefined && savedForm(drafts[day.workDate]) !== savedForm(loadedOf(day)))
    const defaultChanged = defaultDraft !== null && JSON.stringify(defaultDraft) !== JSON.stringify(settings.defaultPeoplePerShift)
    const changes = changedDays.length + (defaultChanged ? 1 : 0)
    const problems = changedDays.flatMap((day) => {
      const problem = draftProblem(day.workDate, drafts[day.workDate])
      return problem === null ? [] : [problem]
    })

    const setDraft = (workDate: string, draft: DayDraft | null): void => {
      setSavedAt(null)
      setDrafts((current) => {
        const next = { ...current }
        if (draft === null) {
          delete next[workDate]
        } else {
          next[workDate] = draft
        }
        return next
      })
    }
    const toDefault = (day: StaffingDay): void => {
      if (day.team.version === 0) {
        setDraft(day.workDate, null)
        return
      }
      const people = defaultPeopleOf(defaults, shifts)
      setDraft(day.workDate, { people, namesText: namesTextOf(people), closed: false, note: '', toDefault: true })
      setNoteOpen(null)
    }
    const changeDefault = (shiftId: ShiftId, by: number): void => {
      setSavedAt(null)
      setDefaultDraft({ ...defaults, [shiftId]: Math.max(0, defaults[shiftId] + by) })
    }
    const discard = (): void => {
      setDrafts({})
      setDefaultDraft(null)
      setNoteOpen(null)
      setSaveError(null)
    }
    const save = async (): Promise<void> => {
      setSaving(true)
      setSaveError(null)
      const savedDays: Array<string> = []
      let failedAt: string | null = null
      let defaultSaved = false
      try {
        if (defaultChanged) {
          failedAt = 'defecto'
          await updateSettings.mutateAsync({ defaultPeoplePerShift: defaults })
          defaultSaved = true
        }
        for (const day of changedDays) {
          failedAt = day.workDate
          const draft = drafts[day.workDate]
          if (draft.toDefault) {
            await reset.mutateAsync({ workDate: day.workDate, expectedVersion: day.team.version })
          } else {
            await update.mutateAsync({
              workDate: day.workDate,
              expectedVersion: day.team.version,
              people: draft.people.map((person) => ({
                ...(UUID.test(person.id) ? { id: person.id } : {}),
                name: person.name.trim(),
                shiftId: person.shiftId === 'tarde' ? 'tarde' : 'manana',
                startsAt: person.startsAt,
              })),
              closed: draft.closed,
              note: draft.note.trim() === '' ? null : draft.note.trim(),
            })
          }
          savedDays.push(day.workDate)
        }
        setSavedAt(Date.now())
        setNoteOpen(null)
      } catch (error) {
        const which = failedAt === null || failedAt === 'defecto' ? 'el valor por defecto' : `el ${dayLabel(failedAt)}`
        setSaveError(getErrorCode(error) === 'CONFLICT'
          ? `Otra persona ha cambiado ${which} mientras lo mirabas. Recarga la pantalla.`
          : `No se ha podido guardar ${which}: ${trpcMessage(error)}`)
      } finally {
        await Promise.all([utils.configuration.mepStaffing.list.invalidate(), utils.configuration.mepSettings.get.invalidate()])
        setDrafts((current) => Object.fromEntries(Object.entries(current).filter(([workDate]) => !savedDays.includes(workDate))))
        if (defaultSaved) {
          setDefaultDraft(null)
        }
        setSaving(false)
      }
    }

    const sameNet = shifts.every((shift) => shift.netMinutes === shifts[0].netMinutes)
    let week: number | null = null
    body = (
      <>
        <div className={local['mep-staffing']}>
          <div className={local['mep-staffing__main']}>
            {header}
            <div className={local['mep-staffing__cards']}>
              <section className={local['mep-staffing__card']} role="group" aria-label="Por defecto">
                <span className={local['mep-staffing__card-label']}>Por defecto</span>
                <div className={local['mep-staffing__defaults']}>
                  {shifts.map((shift) => (
                    <span key={shift.id} className={local['mep-staffing__default']}>
                      <span className={local['mep-staffing__shift']}>{shift.name}</span>
                      <Stepper
                        value={defaults[shift.id]}
                        disabled={isReadOnly}
                        lessLabel={`Una menos por defecto en ${shift.name}`}
                        moreLabel={`Una más por defecto en ${shift.name}`}
                        countLabel={`${shift.name}: ${defaults[shift.id]} personas por defecto`}
                        onLess={() => changeDefault(shift.id, -1)}
                        onMore={() => changeDefault(shift.id, 1)}
                      />
                    </span>
                  ))}
                  <span className={local['mep-staffing__card-text']}>personas por turno si nadie indica otra cosa</span>
                </div>
              </section>
              <section className={local['mep-staffing__card']} aria-label="Con qué se calcula">
                <span className={local['mep-staffing__card-label']}>Con qué se calcula</span>
                <span className={local['mep-staffing__card-text']}>
              Jornada {shifts.map((shift, index) => (
                    <Fragment key={shift.id}>
                      {index > 0 ? ' · ' : ''}{shift.name.toLowerCase()} <b>{shift.start} – {shift.end}</b>{sameNet ? '' : ` (${formatMinutes(shift.netMinutes)} netas)`}
                    </Fragment>
                  ))}{sameNet && shifts.length > 0 ? ` (${formatMinutes(shifts[0].netMinutes)} netas cada uno)` : ''} · lista <b>{settings.marginMin} min</b> antes del paso de cocina · <Link className={local['mep-staffing__card-link']} to={MEP_MARGIN_HEAT_PATH}>Margen y calor ›</Link>
                </span>
                <span className={local['mep-staffing__card-text']}>
              Duraciones <b>reales</b>: histórico de {settings.historyDays} días por máquina y preparación, con al menos {settings.historyMinSamples} muestras (sin histórico, la de MRPeasy)
                </span>
              </section>
            </div>

            <div className={local['mep-staffing__table-wrap']}>
              <div className={local['mep-staffing__table-title']}>
                <h2>Próximos días</h2>
                <span className={local['mep-staffing__legend']}>
                  <span className={local['mep-staffing__legend--short']}>faltan personas</span>
                  <span className={local['mep-staffing__legend--over']}>sobran</span>
                  <span className={local['mep-staffing__legend--even']}>justo</span>
                </span>
              </div>
              <table className={local['mep-staffing__table']}>
                <colgroup>
                  <col className={local['mep-staffing__col-day']} />
                  <col className={local['mep-staffing__col-plan']} />
                  <col className={local['mep-staffing__col-hired']} />
                  <col />
                  <col className={local['mep-staffing__col-need']} />
                  <col className={local['mep-staffing__col-balance']} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Día</th>
                    <th scope="col">Plan de cocina</th>
                    <th scope="col">Contratadas</th>
                    <th scope="col">Nombres previstos</th>
                    <th scope="col" className={local['mep-staffing__need']}>Necesarias</th>
                    <th scope="col"><span className={local['mep-staffing__visually-hidden']}>Balance</span></th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((day) => {
                    const dayWeek = isoWeek(day.workDate)
                    const newWeek = dayWeek !== week
                    week = dayWeek
                    return (
                      <Fragment key={day.workDate}>
                        {newWeek
                          ? <tr className={local['mep-staffing__week']}><td colSpan={6}>Semana {dayWeek}</td></tr>
                          : null}
                        <DayRows
                          day={day}
                          draft={draftOf(day)}
                          shifts={shifts}
                          today={today}
                          selected={day.workDate === selected?.workDate}
                          edited={changedDays.includes(day)}
                          noteOpen={noteOpen === day.workDate}
                          readOnly={isReadOnly}
                          onSelect={() => setSelectedDay(day.workDate)}
                          onChange={(draft) => setDraft(day.workDate, draft)}
                          onDefault={() => toDefault(day)}
                          onOpenNote={(open) => setNoteOpen(open ? day.workDate : null)}
                        />
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {selected !== undefined ? <Forecast day={selected} draft={draftOf(selected)} today={today} /> : null}
        </div>

        {changes > 0 && !isReadOnly
          ? (
            <div className={local['mep-staffing__save']}>
              <span>{changes === 1 ? '1 cambio sin guardar' : `${changes} cambios sin guardar`}</span>
              {problems.length > 0 ? <span className={local['mep-staffing__save-problem']}>{problems[0]}</span> : null}
              {saveError !== null ? <span role="alert" className={local['mep-staffing__save-problem']}>{saveError}</span> : null}
              <Button variant="secondary" label="Descartar" disabled={saving} onClick={discard} />
              <Button label={saving ? 'Guardando…' : 'Guardar'} disabled={saving || problems.length > 0} onClick={() => void save()} />
            </div>
          )
          : null}
        {savedAt !== null && changes === 0
          ? <div role="status" className={local['mep-staffing__saved']}>Guardado · queda en el historial de configuración</div>
          : null}
      </>
    )
  }

  return <section className={styles['admin-page']}>{body}</section>
}
